(() => {
  if (customElements.get('cavador-flooring-compare')) return;

  const fields = ['material', 'collection', 'model', 'colour', 'finish', 'dimensions', 'thickness', 'wear_layer', 'water_resistance', 'underpad', 'installation', 'warranty', 'coverage', 'coverage_note'];
  const instances = new Set();
  const activeInstance = () => [...instances].find(instance => instance.isConnected);
  window.CavadorFlooringComparison = Object.freeze({
    getSelected: () => activeInstance()?.getSelected() || [],
    getProduct: handle => activeInstance()?.getProduct(handle) || null
  });

  const cleanText = value => typeof value === 'string' ? value.trim().slice(0, 600) : '';

  const productUrl = value => {
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9-]+\/?$/i.test(url.pathname)) return '';
      return url.pathname;
    } catch {
      return '';
    }
  };

  const imageUrl = value => {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      if (url.protocol !== 'https:' || (url.hostname !== 'cdn.shopify.com' && !(url.origin === window.location.origin && url.pathname.startsWith('/cdn/shop/')))) return '';
      return url.href;
    } catch {
      return '';
    }
  };

  const sanitize = input => {
    if (!input || typeof input !== 'object' || !/^\d+$/.test(String(input.id)) || !/^[a-z0-9-]+$/.test(input.handle || '')) return null;
    const title = cleanText(input.title);
    const url = productUrl(input.url);
    if (!title || !url) return null;
    const values = {};
    fields.forEach(field => {
      if (field === 'coverage') values[field] = typeof input.values?.coverage === 'number' && Number.isFinite(input.values.coverage) && input.values.coverage > 0 ? input.values.coverage : null;
      else values[field] = cleanText(input.values?.[field]);
    });
    if (/^TF[A-Z]*\d+[A-Z0-9]*$/i.test(values.colour.replace(/[\s_-]/g, ''))) values.colour = '';
    const positive = value => typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
    const integer = value => Number.isSafeInteger(value) && value > 0 ? value : null;
    return {
      id: String(input.id), handle: input.handle, title, url, image: imageUrl(input.image),
      coveragePerBox: positive(input.coveragePerBox) || values.coverage,
      planksPerBox: integer(input.planksPerBox), priceCents: integer(input.priceCents), values
    };
  };

  customElements.define('cavador-flooring-compare', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.hub = this.closest('cavador-flooring-hub');
      this.scope = this.hub || this;
      this.dialog = this.querySelector('[data-floor-compare-dialog]');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      try {
        this.labels = JSON.parse(this.querySelector('[data-floor-compare-labels]').textContent);
      } catch {
        return;
      }
      this.initialized = true;
      this.abort = new AbortController();
      this.storageKey = 'cavador-flooring-compare:v1:' + (this.dataset.locale || 'en');
      this.catalog = new Map();
      this.controls = new Map();
      this.selected = new Map();
      this.tray = this.querySelector('[data-floor-compare-tray]');
      this.list = this.querySelector('[data-floor-compare-shortlist]');
      this.count = this.querySelector('[data-floor-compare-count]');
      this.hint = this.querySelector('[data-floor-compare-hint]');
      this.openButton = this.querySelector('[data-floor-compare-open]');
      this.clearButton = this.querySelector('[data-floor-compare-clear]');
      this.closeButton = this.querySelector('[data-floor-compare-close]');
      this.tableHost = this.querySelector('[data-floor-compare-table]');
      this.status = this.querySelector('[data-floor-compare-status]');
      this.entryButton = this.querySelector('[data-floor-compare-entry]');
      this.picker = this.querySelector('[data-floor-compare-picker]');
      this.search = this.querySelector('[data-floor-compare-search]');
      this.results = this.querySelector('[data-floor-compare-results]');
      this.pickerCount = this.querySelector('[data-floor-compare-results-count]');
      this.pickerState = this.querySelector('[data-floor-compare-picker-state]');
      this.moreButton = this.querySelector('[data-floor-compare-more]');
      this.pickerLimit = 12;
      this.numberFormat = new Intl.NumberFormat(this.dataset.locale || 'en', { maximumFractionDigits: 2 });
      instances.add(this);
      this.readCatalog();
      this.current = this.catalog.get(this.dataset.currentId);
      this.restore();
      this.bind();
      this.render();
      if (this.entryButton) this.entryButton.hidden = false;
      if (this.picker && [...this.selected.keys()].some(id => !this.catalog.has(id))) this.loadCatalog();
    }

    disconnectedCallback() {
      this.abort?.abort();
      instances.delete(this);
      if (this.dialog?.open) this.dialog.close();
      document.body.classList.remove('cfh-compare-is-open');
      clearTimeout(this.statusTimer);
      this.initialized = false;
    }

    readCatalog(scope = this.scope, bindControls = true) {
      scope.querySelectorAll('[data-floor-compare-product]').forEach(script => {
        let record;
        let input;
        try { input = JSON.parse(script.textContent); record = sanitize(input); } catch { return; }
        if (!record) return;
        ['wear_layer', 'warranty', 'coverage_note'].forEach(field => {
          const label = cleanText(input.field_labels?.[field]);
          if (label) this.labels.fields[field] = label;
        });
        const card = script.closest('[data-card]');
        const template = script.nextElementSibling?.matches('[data-floor-compare-facts]') ? script.nextElementSibling : null;
        template?.content.querySelectorAll('dl > div').forEach(row => {
          const label = row.querySelector('dt')?.textContent.trim();
          const value = cleanText(row.querySelector('dd')?.textContent);
          ['water_resistance', 'underpad'].forEach(field => {
            if (label === this.labels.fields[field]) record.values[field] = value;
          });
        });
        this.catalog.set(record.id, record);
        if (this.selected.has(record.id)) this.selected.set(record.id, record);
        const control = bindControls ? card?.querySelector('[data-floor-compare-toggle]') : null;
        if (!control) return;
        this.controls.set(record.id, control);
        control.closest('[data-floor-compare-pick]').hidden = false;
      });
    }

    getSelected() {
      return [...this.selected.values()].map(record => ({ ...record, values: { ...record.values } }));
    }

    getProduct(handle) {
      const record = [...this.catalog.values()].find(record => record.handle === handle);
      return record ? { ...record, values: { ...record.values } } : null;
    }

    publishChange() {
      const products = this.getSelected();
      const signature = JSON.stringify(products);
      if (signature === this.lastSelection) return;
      this.lastSelection = signature;
      document.dispatchEvent(new CustomEvent('cavador:comparison-change', { detail: { products } }));
    }

    async loadCatalog() {
      if (!this.dataset.catalogUrl || this.catalogLoaded) return;
      if (this.catalogPromise) return this.catalogPromise;
      this.pickerState.textContent = this.labels.picker_loading;
      this.pickerState.hidden = false;
      if (this.dialog.open) this.announce(this.labels.picker_loading);
      this.catalogPromise = (async () => {
        try {
          const url = new URL(this.dataset.catalogUrl, window.location.origin);
          if (url.origin !== window.location.origin) throw new Error('Invalid catalog origin');
          const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
          if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
          const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: this.abort.signal });
          if (!response.ok) throw new Error('Catalog unavailable');
          const document = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (!document.querySelector('[data-floor-compare-product]')) throw new Error('Catalog records unavailable');
          this.readCatalog(document, false);
          // The freshly rendered current product remains authoritative on its page.
          if (this.current) {
            this.catalog.set(this.current.id, this.current);
            if (this.selected.has(this.current.id)) this.selected.set(this.current.id, this.current);
          }
          this.catalogLoaded = true;
          this.pickerState.hidden = true;
          this.render();
          if (this.dialog.open) {
            this.renderTable();
            this.announce(this.pickerCount.textContent);
          }
        } catch (error) {
          if (error.name === 'AbortError') return;
          this.pickerState.textContent = this.labels.picker_error;
          this.pickerState.hidden = false;
          if (this.dialog.open) this.announce(this.labels.picker_error);
        } finally {
          this.catalogPromise = null;
        }
      })();
      return this.catalogPromise;
    }

    add(record) {
      if (!record) return false;
      if (this.selected.size >= 3 && !this.selected.has(record.id)) {
        this.hint.textContent = this.labels.limit;
        this.announce(this.labels.limit);
        return false;
      }
      this.selected.set(record.id, record);
      this.render();
      if (this.dialog.open) this.renderTable();
      this.announce(this.labels.added.replace('__TITLE__', record.title));
      return true;
    }

    restore() {
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
        if (saved?.version !== 1 || !Array.isArray(saved.selected)) return;
        saved.selected.slice(0, 3).forEach(item => {
          const record = sanitize(item);
          if (record) this.selected.set(record.id, this.catalog.get(record.id) || record);
        });
      } catch {}
    }

    save() {
      try {
        if (this.selected.size) sessionStorage.setItem(this.storageKey, JSON.stringify({ version: 1, selected: [...this.selected.values()] }));
        else sessionStorage.removeItem(this.storageKey);
      } catch {}
    }

    bind() {
      const options = { signal: this.abort.signal };
      this.scope.addEventListener('change', event => {
        const control = event.target.closest('[data-floor-compare-toggle]');
        if (!control) return;
        const record = this.catalog.get(control.value);
        if (!record) return;
        if (!control.checked) {
          this.remove(record.id);
          return;
        }
        if (!this.add(record)) control.checked = false;
      }, options);
      this.addEventListener('click', event => {
        const pick = event.target.closest('[data-floor-compare-add]');
        if (pick) {
          const record = this.catalog.get(pick.dataset.floorCompareAdd);
          if (this.selected.has(record?.id)) this.remove(record.id);
          else this.add(record);
          this.results.querySelector(`[data-floor-compare-add="${pick.dataset.floorCompareAdd}"]`)?.focus({ preventScroll: true });
        }
      }, options);
      this.openButton.addEventListener('click', () => this.open(), options);
      this.entryButton?.addEventListener('click', () => {
        if (this.current && !this.selected.has(this.current.id)) this.add(this.current);
        this.open();
      }, options);
      this.search?.addEventListener('input', () => {
        this.pickerLimit = 12;
        this.renderPicker();
        this.announce(this.pickerCount.textContent);
      }, options);
      this.moreButton?.addEventListener('click', () => {
        this.pickerLimit += 12;
        this.renderPicker();
      }, options);
      this.clearButton.addEventListener('click', () => {
        this.selected.clear();
        this.render();
        if (this.picker && this.dialog.open) {
          this.renderTable();
          this.search.focus({ preventScroll: true });
        } else this.close();
        this.announce(this.labels.clear_status);
        if (!this.dialog.open) this.focusVisible();
      }, options);
      this.closeButton.addEventListener('click', () => this.close(), options);
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.close();
      }, options);
      this.dialog.addEventListener('close', () => document.body.classList.remove('cfh-compare-is-open'), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.close();
      }, options);
      window.addEventListener('pageshow', () => {
        this.close();
        this.selected.clear();
        this.restore();
        this.render();
      }, options);
    }

    announce(message) {
      clearTimeout(this.statusTimer);
      this.status.textContent = '';
      this.statusTimer = setTimeout(() => { this.status.textContent = message; }, 20);
    }

    isVisibleControl(element) {
      if (!element?.isConnected || element.disabled || !element.matches('button, a[href], input, select, textarea, summary, [tabindex]')) return false;
      for (let ancestor = element; ancestor; ancestor = ancestor.parentElement) {
        if (ancestor.hidden || ancestor.hasAttribute('inert') || ancestor.matches('dialog:not([open])')) return false;
        if (ancestor.matches('details:not([open])') && !ancestor.querySelector('summary')?.contains(element)) return false;
        const style = getComputedStyle(ancestor);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
      }
      return true;
    }

    focusVisible(preferred = []) {
      const candidates = [...preferred, this.entryButton, ...this.controls.values(), this.scope.querySelector('[data-search]'), this.scope.querySelector('[data-filter-panel] > summary')];
      candidates.find(element => this.isVisibleControl(element))?.focus({ preventScroll: true });
    }

    text(tag, className, value) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      element.textContent = value;
      return element;
    }

    image(record, className) {
      if (!record.image) return this.text('span', className === 'cfh-compare__thumb' ? className : 'cfh-compare__finish-placeholder', className === 'cfh-compare__thumb' ? '' : this.labels.no_image);
      const image = document.createElement('img');
      image.className = className;
      image.src = record.image;
      image.alt = className === 'cfh-compare__thumb' ? '' : record.title;
      image.width = className === 'cfh-compare__thumb' ? 42 : 640;
      image.height = className === 'cfh-compare__thumb' ? 42 : 480;
      image.decoding = 'async';
      return image;
    }

    removeButton(record) {
      const button = this.text('button', 'cfh-compare__remove', '×');
      button.type = 'button';
      button.dataset.floorCompareRemove = record.id;
      button.setAttribute('aria-label', this.labels.remove.replace('__TITLE__', record.title));
      // Own this action on the generated control: modal/theme handlers can stop
      // bubbling before a click reaches the surrounding comparison element.
      button.addEventListener('click', event => {
        event.preventDefault();
        this.remove(record.id);
      }, { signal: this.abort.signal });
      return button;
    }

    render() {
      this.save();
      this.controls.forEach((control, id) => { control.checked = this.selected.has(id); });
      this.scope.classList.toggle('has-floor-compare', this.selected.size > 0);
      this.tray.hidden = this.selected.size === 0;
      this.count.textContent = this.labels.selected.replace('__COUNT__', this.selected.size);
      this.hint.textContent = this.selected.size < 2 ? this.labels.add_one : this.labels.choose;
      this.openButton.disabled = this.picker ? false : this.selected.size < 2;
      const fragment = document.createDocumentFragment();
      this.selected.forEach(record => {
        const item = document.createElement('li');
        const copy = document.createElement('div');
        copy.className = 'cfh-compare__short-name';
        copy.append(this.text('span', '', record.title), this.text('span', '', record.values.model || record.title));
        item.append(this.image(record, 'cfh-compare__thumb'), copy, this.removeButton(record));
        fragment.append(item);
      });
      this.list.replaceChildren(fragment);
      this.renderPicker();
      this.publishChange();
    }

    renderPicker() {
      if (!this.picker) return;
      const words = this.search.value.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
      const matches = [...this.catalog.values()].filter(record => {
        const text = [record.title, ...Object.values(record.values)].join(' ').toLocaleLowerCase();
        return words.every(word => text.includes(word));
      });
      this.pickerCount.textContent = this.labels.picker_results.replace('__COUNT__', matches.length);
      this.moreButton.hidden = matches.length <= this.pickerLimit;
      const fragment = document.createDocumentFragment();
      matches.slice(0, this.pickerLimit).forEach(record => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cfh-compare__choice';
        button.dataset.floorCompareAdd = record.id;
        const selected = this.selected.has(record.id);
        button.setAttribute('aria-pressed', String(selected));
        const copy = document.createElement('span');
        copy.className = 'cfh-compare__choice-copy';
        copy.append(this.text('span', '', record.title), this.text('span', '', [record.values.model, record.values.material].filter(Boolean).join(' · ')));
        button.append(this.image(record, 'cfh-compare__thumb'), copy,
          this.text('span', 'cfh-compare__choice-action', selected ? this.labels.picker_selected : this.labels.picker_add));
        item.append(button);
        fragment.append(item);
      });
      if (!matches.length) fragment.append(this.text('li', 'cfh-compare__picker-empty', this.labels.picker_empty));
      this.results.replaceChildren(fragment);
    }

    remove(id) {
      const record = this.selected.get(id);
      if (!record) return;
      const index = [...this.selected.keys()].indexOf(id);
      const inDialog = this.dialog.open;
      const focusedRemove = document.activeElement?.dataset.floorCompareRemove === id;
      this.selected.delete(id);
      this.render();
      if (inDialog) {
        if (this.selected.size < 2 && !this.picker) this.close();
        else {
          this.renderTable();
          const buttons = [...this.tableHost.querySelectorAll('[data-floor-compare-remove]')];
          (buttons[Math.min(index, buttons.length - 1)] || this.search)?.focus({ preventScroll: true });
        }
      } else if (focusedRemove) {
        this.focusVisible([this.list.querySelector('[data-floor-compare-remove]'), this.controls.get(id)]);
      }
      this.announce(this.labels.removed.replace('__TITLE__', record.title));
    }

    renderTable() {
      const records = [...this.selected.values()];
      if (!records.length) {
        this.tableHost.replaceChildren(this.text('p', 'cfh-compare__picker-empty', this.labels.add_one));
        return;
      }
      const table = document.createElement('table');
      table.className = 'cfh-compare__table';
      table.dataset.floorCount = records.length;
      const head = document.createElement('thead');
      const headRow = document.createElement('tr');
      const corner = this.text('th', '', this.labels.details);
      corner.scope = 'col';
      headRow.append(corner);
      records.forEach(record => {
        const cell = document.createElement('th');
        cell.scope = 'col';
        const finish = document.createElement('div');
        finish.className = 'cfh-compare__finish';
        finish.append(this.image(record, 'cfh-compare__finish-image'), this.removeButton(record));
        cell.append(finish, this.text('span', 'cfh-compare__finish-title', record.title), this.text('span', 'cfh-compare__finish-model', record.values.model || ''));
        const link = this.text('a', 'cfh-compare__view', this.labels.view);
        link.href = record.url;
        link.setAttribute('aria-label', this.labels.view + ': ' + record.title);
        cell.append(link);
        headRow.append(cell);
      });
      head.append(headRow);
      const body = document.createElement('tbody');
      fields.forEach(field => {
        if (field === 'coverage_note' && !records.some(record => record.values.coverage_note)) return;
        const row = document.createElement('tr');
        const label = this.text('th', '', this.labels.fields[field]);
        label.scope = 'row';
        row.append(label);
        records.forEach(record => {
          const value = field === 'coverage' && record.values.coverage ? this.labels.sqft.replace('__AMOUNT__', this.numberFormat.format(record.values.coverage)) : field === 'coverage' ? '' : record.values[field];
          row.append(this.text('td', value ? '' : 'cfh-compare__missing', value || this.labels.missing));
        });
        body.append(row);
      });
      table.append(head, body);
      this.tableHost.replaceChildren(table);
    }

    open() {
      if ((!this.picker && this.selected.size < 2) || this.dialog.open) return;
      this.returnFocus = document.activeElement;
      this.renderTable();
      this.tableHost.scrollLeft = 0;
      this.dialog.showModal();
      document.body.classList.add('cfh-compare-is-open');
      (this.search || this.closeButton).focus({ preventScroll: true });
      if (this.picker) this.loadCatalog();
    }

    close() {
      if (!this.dialog.open) return;
      this.dialog.close();
      document.body.classList.remove('cfh-compare-is-open');
      this.focusVisible([this.returnFocus, this.clearButton]);
    }
  });
})();
